import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { MDBDropdown, MDBDropdownItem, MDBDropdownLink, MDBDropdownMenu, MDBDropdownToggle, MDBIcon, MDBInput, MDBTooltip } from 'mdb-react-ui-kit';
import { Glyph, GlyphLayer, IdeaBox, Task, TaskType, VisualisationAttribute } from "konect-model";
import { IdeaBoxEntry } from "konect-model";
import { Project } from "konect-model";
import UserControlService from "../../services/user-control-service";
import { nanoid } from "nanoid";
import "./GlyphGrouping.css"
import { AppContext, NavState } from "../App";
import AcceptModal from "./AcceptModal";
import DropdownSelect from "./DropdownSelect";

interface GlyphGroupingProps {
    project: Project;
    glyphs: Glyph[];
    backlog: IdeaBoxEntry[];
    onRemove: (id: string) => void;
    onDraw: (id: string) => void;
    onCreate: (name: string, description: string) => void;
    setGlyphs: (glyphs: Glyph[]) => void;
    setBacklog: (entries: IdeaBoxEntry[]) => void;
    generateGlyphs: Function;
}

export enum DropType {
    LAYER = "LAYER",
    BACKLOG = "BACKLOG",
    GLYPH = "GLYPH",
    TASK = "TASK"
}

export default function GlyphGrouping(props: GlyphGroupingProps) {
    const { project, setProject, setEditingTask } = useContext(AppContext);
    const [current, setCurrent] = useState(0);
    const sliderRef = useRef<HTMLDivElement>(null);
    const [glyphInput, setGlyphInput] = useState("");
    const [editGlyph, setEditGlyph] = useState(-1);
    const glyphInputWrapperRef = useRef<HTMLDivElement>(null);
    const glyphInputRef = useRef("");
    const measureSpanRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [filteredBacklog, setFilteredBacklog] = useState<IdeaBoxEntry[]>(props.backlog);
    const [filterState, setFilterState] = useState(0);
    const [filterString, setFilterString] = useState("");

    enum FilterState {
        ALL = 0,
        INFORMATION = 1,
        INTERACTION = 2,
    }

    useEffect(() => {
        let entries = props.backlog;
        if (filterState === FilterState.INFORMATION) entries = entries.filter((ibe) => ibe.type === TaskType.INFORMATION);
        if (filterState === FilterState.INTERACTION) entries = entries.filter((ibe) => ibe.type === TaskType.INTERACTION);
        if (filterString !== "") entries = entries.filter((ibe) => ibe.task.name.toLowerCase().includes(filterString));
        setFilteredBacklog(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.backlog, filterState, filterString])

    useEffect(() => {
        glyphInputRef.current = glyphInput;
    }, [glyphInput])

    useEffect(() => {
        if (measureSpanRef.current && inputRef.current) {
            inputRef.current.style.width = `${measureSpanRef.current.offsetWidth + 8}px`;
        }
    }, [glyphInput, editGlyph]);  // Adjust the width every time inputValue changes


    // #########################################################
    // # DRAG AND DROP FUNCTIONS
    // #########################################################

    const onDragEnd = (result: any) => {
        const { destination, source } = result;

        if (!destination) return; //dropped in a non-droppable area
        if (destination.droppableId === source.droppableId && destination.index === source.index) return; // dropped in its exact origin
        if (destination.droppableId === "backlog" && source.droppableId === "backlog") return; //don't allow reordering of the backlog

        UserControlService.update();

        const sourceEntries = getEntriesOf(source);
        const destinationEntries = getEntriesOf(destination);

        const movedEntry = sourceEntries.splice(source.index, 1)[0];
        destinationEntries.splice(destination.index, 0, movedEntry);

        setEntriesOf(source, sourceEntries);
        setEntriesOf(destination, destinationEntries);
    }

    const switchEntry = (source: { droppableId: string, index: number }) => {
        let destination = {};

        if (source.droppableId === "backlog") destination = { droppableId: props.glyphs[current].id, index: 0 };
        else destination = { droppableId: "backlog", index: 0 }

        onDragEnd({ destination, source })

        props.setGlyphs([...props.glyphs]);
        props.setBacklog([...props.backlog]);
    }


    // #########################################################
    // # HELPER FUNCTIONS
    // #########################################################

    const getEntriesOf = (target: any) : IdeaBoxEntry[] => {
        if (target.droppableId === "backlog") return props.backlog;
        else {
            const glyph = props.glyphs.find((gl: Glyph) => target.droppableId === gl.id);
            if (!glyph) return [];
            return getEntriesOfLayers(glyph);
        }
    }

    const setEntriesOf = (target: any, entries: IdeaBoxEntry[]) => {
        if (target.droppableId === "backlog") props.setBacklog([...entries]);
        else {
            const glyph = props.glyphs.find((gl: Glyph) => target.droppableId === gl.id);
            if (!glyph) return;
            const layers: GlyphLayer[] = [];
            for (const entry of entries) {
                const layer = new GlyphLayer(nanoid());
                layer.entries.push(entry);
                layers.push(layer);
            }
            glyph.layers = layers;
        }
    }

    const scrollCarouselTo = (index: number) => {
        setCurrent(index);
        if (sliderRef.current) sliderRef.current.scrollTo({ left: sliderRef.current.offsetWidth * index + 20 * index, behavior: 'smooth' });
        document.querySelectorAll('.glyphTable').forEach(element => element.scrollTop = 0);
    }

    const getAttributeLabel = (entry : IdeaBoxEntry) => {
        let attributeLabel = "";
        if (entry.insight && entry.selectedAttribute && entry.availableAttributes) {
            attributeLabel += entry.selectedAttribute.name;
            attributeLabel += " (" + entry.availableAttributes.findIndex((att : VisualisationAttribute) => att.id === entry.selectedAttribute.id) + ")";
        }
        return attributeLabel;
    }

    const getEntriesOfLayers = (glyph: Glyph) : IdeaBoxEntry[] => {
        const entries:IdeaBoxEntry[] = [];
        for (const layer of glyph.layers) {
            if (layer.entries) {
                for (const entry of layer.entries) entries.push(entry);
            }
        }
        return entries;
    }


    // #########################################################
    // # EDITOR FUNCTIONALITY
    // #########################################################

    const renameGlyph = (glyph: Glyph, index: number) => {
        setGlyphInput(glyph.name);
        setEditGlyph(index);
        setTimeout(() => glyphInputWrapperRef.current?.focus(), 0);

        const closeInput = () => {
            setEditGlyph(-1);
            UserControlService.update();
            props.glyphs[index].name = glyphInputRef.current;
            props.setGlyphs([...props.glyphs]);
        }
        const handleClick = (event:any) => {
            if (glyphInputWrapperRef.current && !glyphInputWrapperRef.current.contains(event.target)) {
                closeInput();
                window.removeEventListener('mousedown', handleClick);
            } 
        }
        const handleEnter = (event:any) => {
            if (event.key === 'Enter') {
                closeInput();
                window.removeEventListener('keydown', handleEnter);
            }
        }
        window.addEventListener('mousedown', handleClick);
        window.addEventListener('keydown', handleEnter);
    }

    const updateIdeaBoxEntry = (event : any, entry: IdeaBoxEntry) => {
        event.preventDefault();
        UserControlService.update();
        switch (event.target.name){
        case "insight": 
            const insight =  JSON.parse(event.target.dataset["mssg"]);
            entry.insight = insight;
            entry.visualLevel = insight.visualLevel;
            entry.availableAttributes = project.getAttributesOfInsight(insight);
            entry.selectedAttribute = entry.availableAttributes[0];
            break;
        case "selectedAttribute":   
            const attribute =  JSON.parse(event.target.dataset["mssg"]);
            entry.selectedAttribute = attribute;
            break;
        default:
            //@ts-ignore
            entry[event.target.name] = event.target.value;
        }
        IdeaBox.updateEntry(project.ideaBox, entry.id, {...entry});
        setProject(project);
    }

    const goToTask = (id: string) => {
        UserControlService.changeView(NavState.TASK);
        setTimeout(() => {
            var container = document.getElementById("graph-container");
            var node = document.getElementById(id);
            if (container && node) {
                const containerRect = container.getBoundingClientRect();
                const nodeRect = node.getBoundingClientRect();
                const verticalScrollPosition = nodeRect.top - containerRect.top + container.scrollTop - (containerRect.height / 2) + (nodeRect.height / 2);
                const horizontalScrollPosition = nodeRect.left - containerRect.left + container.scrollLeft - (containerRect.width / 2) + (nodeRect.width / 2);
                container.scrollTop = verticalScrollPosition;
                container.scrollLeft = horizontalScrollPosition;
                //@ts-ignore
                setTimeout(() => {node.style.backgroundColor = "#FE3452";}, 500);
                //@ts-ignore
                setTimeout(() => {node.style.backgroundColor = null}, 4500);
            }
        }, 1);
    }

    const editEntry = (event: Event, entry: IdeaBoxEntry) => {
        event.stopPropagation();
        const task = Task.findTask(project.taskModel.rootTask, entry.task.id);
        if (!task) return;
        goToTask(task.id);
        setEditingTask(task);
    }

    const duplicateEntry = (entry: IdeaBoxEntry) => {
        UserControlService.update();
        const task = Task.findTask(project.taskModel.rootTask, entry.task.id);
        if (!task) return;
        const { newIdeaBoxEntries } = Task.addTaskRule(project, task, nanoid(), entry.interfaceElement, [entry.type], project.ideaBox.entries.indexOf(entry));
        const newIdeaBoxEntry:IdeaBoxEntry = newIdeaBoxEntries[0];
        if (entry.insight) {
            newIdeaBoxEntry.insight = entry.insight;
            newIdeaBoxEntry.visualLevel = entry.insight.visualLevel;
            newIdeaBoxEntry.availableAttributes = project.getAttributesOfInsight(entry.insight);
            newIdeaBoxEntry.selectedAttribute = entry.selectedAttribute;
        }
        for (const glyph of project.glyphs) {
            let layer = glyph.findLayerOfTask(entry);
            if (layer) {
                layer.entries.push(newIdeaBoxEntry);
                break;
            }
        }
        setProject(project);
    }

    const deleteEntry = (event: Event, entry: IdeaBoxEntry) => {
        event.stopPropagation();
        UserControlService.update();
        const task = Task.findTask(project.taskModel.rootTask, entry.task.id);
        if (!task) return;
        Task.removeTaskRule(project, task, entry.ruleId);
        setProject(project);
    }


    // #########################################################
    // # RENDERING
    // #########################################################

    const renderGlyphs = useMemo(() => {
        if (props.glyphs.length < 1) {
            //@ts-ignore
            return <div className={"help-info"}>
                Create a Glyph to get started<br />
                {(project.taskModel && project.taskModel.resourceModel && project.taskModel.resourceModel.typeDefinition) &&
                    <span onClick={() => { props.generateGlyphs() }} className="auto-create">AUTO CREATE</span>}
            </div>
        }

        return props.glyphs.map((glyph: Glyph, index: number) => {
            return <div className="glyphTable" key={glyph.id}>
                <div className="taskRow taskRowHeader">
                    <div></div>
                    <div title="Type">Type</div>
                    <div title="Task Name">Task Name</div>
                    <div title="Interface Element">Interface Element</div>
                    <div title="Insight">Insight</div>
                    <div title="Selected Attribute">Selected Attribute</div>
                </div>
                <Droppable droppableId={glyph.id}>
                    {(provided) => (
                        <div className="rowGrow" ref={provided.innerRef} {...provided.droppableProps}>
                            {getEntriesOfLayers(glyph).map((entry, index) => {
                                return (
                                    <Draggable key={"tr_" + entry.id} draggableId={entry.id} index={index}>
                                        {(provided) => (
                                            <div {...provided.draggableProps} ref={provided.innerRef}>
                                                {renderIBE(entry, { droppableId: glyph.id, index: index }, provided)}
                                            </div> 
                                        )}
                                    </Draggable>
                                )
                            })}
                            {(glyph.layers.length === 0 || glyph.layers[0].entries.length === 0) &&
                                <div className="rowGrow rowPlaceholder" style={{textAlign: "center", fontStyle: "italic", width: "100%"}}>
                                    Add/sort tasks by dragging them here!
                                </div>
                            }
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        })

    }, [props.glyphs]);  // eslint-disable-line react-hooks/exhaustive-deps

    const renderIBE = (entry: IdeaBoxEntry, source: {droppableId: string, index:number}, provided: any) => {
        return (
            <div className="taskRow" {...provided.dragHandleProps} onDoubleClick={() => switchEntry(source)}>
                <div>
                    <MDBIcon fas icon="bars" />
                </div>
                <div title={entry.type === TaskType.INTERACTION ? "Interaction" : "Information"}> {/* Type Icon */}
                    {entry.type === TaskType.INTERACTION && <div className={`graphIconBorder graphIconInteraction`} style={{ borderRadius: "500px" }}>
                        <MDBIcon fas icon="hand-paper" style={{ color: "#ffffff" }} />
                    </div>}
                    {entry.type === TaskType.INFORMATION && <div className={`graphIconBorder graphIconInformation`} style={{ borderRadius: "500px" }}>
                        <MDBIcon fas icon="eye" style={{ color: "#ffffff" }} />
                    </div>}
                </div>
                <div title={entry.task.name}>{entry.task.name}</div> {/* Task */}
                <div title={entry.interfaceElement}>{entry.interfaceElement}</div> {/* Interface Element */}
                <div title={entry.insight ? entry.insight.name : ""}>
                    <DropdownSelect
                        type="insight"
                        value={entry.insight && entry.insight.name}
                        defaultValue={entry.insight ? entry.insight.name : "select insight"}
                        items={project.insights.filter(el => el.type === entry.type || (!el.type && entry.type === TaskType.INFORMATION))}
                        onClick={(e) => updateIdeaBoxEntry(e, entry)}
                    />
                </div> {/* Insight */}
                <div title={getAttributeLabel(entry)}>
                    {entry.insight && <DropdownSelect
                        type="selectedAttribute"
                        value={getAttributeLabel(entry)}
                        defaultValue={"select attribute"}
                        items={entry.availableAttributes}
                        onClick={(e) => updateIdeaBoxEntry(e, entry)}
                    />}
                </div> {/* Selected Attribute */}
                <div> {/* Menu Buttons */}
                    <MDBDropdown style={{zIndex: 0}}>
                        <MDBDropdownToggle tag="button" className="menuToggle">
                            <MDBIcon size={"lg"} fas icon="ellipsis-h" />
                        </MDBDropdownToggle>
                        <MDBDropdownMenu>
                            <MDBDropdownItem>
                                <MDBDropdownLink className="menuLink" onClick={() => goToTask(entry.task.id)}>
                                    <MDBIcon fas icon='sign-in-alt' /> Go to task
                                </MDBDropdownLink>
                            </MDBDropdownItem>
                            <MDBDropdownItem>
                                <MDBDropdownLink onClick={(event:Event) => editEntry(event, entry)}>
                                    <MDBIcon fas icon='pen'/> Edit task
                                </MDBDropdownLink>
                            </MDBDropdownItem>
                            <MDBDropdownItem>
                                <MDBDropdownLink onClick={(event:Event) => duplicateEntry(entry)}>
                                <MDBIcon fas icon='copy' /> Duplicate
                                </MDBDropdownLink>
                            </MDBDropdownItem>
                            <MDBDropdownItem>
                                <MDBDropdownLink onClick={(event:Event) => deleteEntry(event, entry)}>
                                    <MDBIcon fas icon='trash' /> Delete
                                </MDBDropdownLink>
                            </MDBDropdownItem>
                        </MDBDropdownMenu>
                    </MDBDropdown>
                </div> 
            </div>
        )
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="groupingContainer">
                <div className="backlogRow">
                    <h3>Backlog</h3>
                    <div className="backlogTable">
                        <div className="taskRow taskRowHeader">
                            <div></div>
                            <div className="columnHeading" title="Type">
                                <span>Type</span>
                                <div className={`filterButton filter_${filterState}`} onClick={() => setFilterState((filterState + 1) % 3)}>
                                    <MDBIcon fas icon="filter" />
                                </div>
                            </div>
                            <div className="columnHeading" title="Task Name">
                                <span>Task Name</span>
                                <div className="searchInput">
                                    <MDBInput
                                        id='search-input'
                                        /* @ts-ignore */
                                        label={<><MDBIcon fas icon="search" /> Search</>}
                                        type="text" value={filterString}
                                        onChange={(e:any) => setFilterString(e.target.value.toLowerCase())}
                                    />
                                    {filterString && <div onClick={() => {setFilterString(""); document.getElementById('search-input')?.blur();}} id='clear-textfield' className="position-absolute end-0 h-100 d-flex align-items-center justify-content-center" style={{width: "35px"}}>
                                        <i className="fas fa-times secondary"></i>
                                    </div>}
                                </div> 
                            </div>
                            <div title="Interface Element">Interface Element</div>
                            <div title="Insight">Insight</div>
                            <div title="Selected Attribute">Selected Attribute</div>
                        </div>
                        <Droppable droppableId="backlog">
                            {(provided) => (
                                <div className="rowGrow" ref={provided.innerRef} {...provided.droppableProps}>
                                    {props.backlog.map((entry, index) => {
                                        if (filteredBacklog.includes(entry)) {
                                            return (
                                                <Draggable key={"tr_" + entry.id} draggableId={entry.id} index={index}>
                                                    {(provided) => (
                                                        <div {...provided.draggableProps} ref={provided.innerRef}>
                                                            {renderIBE(entry, { droppableId: "backlog", index: index }, provided)}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        }
                                        return null;
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                </div>
                <div className="glyphRow">
                    <div className="glyphHeader">
                        <div className="d-flex flex-row">
                            <h3>{props.glyphs.length === 0 ? "Glyphs" : editGlyph !== current ? `Glyph: ${props.glyphs[current].name}`: "Glyph: "}</h3>
                            {editGlyph === current && <div ref={glyphInputWrapperRef}>
                                <input
                                    autoFocus
                                    value={glyphInput}
                                    onChange={(e) => setGlyphInput(e.target.value)}
                                    ref={inputRef}
                                ></input>
                                <span ref={measureSpanRef}>{glyphInput || ' '}</span>
                            </div>}
                        </div>
                        {props.glyphs.length > 0 && <div className="d-flex flex-row" style={{gap: "2px"}}>
                            <MDBTooltip tag='span' wrapperClass='d-inline-block' title='Rename'>
                                <span className='accept-modal-button' onClick={() => renameGlyph(props.glyphs[current], current)}>
                                    <MDBIcon fas icon='pen' />
                                </span>
                            </MDBTooltip>
                            <MDBTooltip tag='span' wrapperClass='d-inline-block' title='Draw'>
                                <span className='accept-modal-button' onClick={() => props.onDraw(props.glyphs[current].id)}>
                                    <MDBIcon fas icon='brush' />
                                </span>
                            </MDBTooltip>
                            <MDBTooltip tag='span' wrapperClass='d-inline-block' title='Delete'>
                                <AcceptModal imgType={"trash"} direction="right" onAccept={() => {setCurrent(Math.max(current-1,0)); scrollCarouselTo(Math.max(current-1,0)); props.onRemove(props.glyphs[current].id)}} />
                            </MDBTooltip>
                        </div>}
                    </div>
                    <div className="glyphPicker">
                        {props.glyphs.map((glyph, index) => {
                            return <div
                                key={`btn_${glyph.id}`}
                                className={current === index ? "active" : ""}
                                onClick={() => scrollCarouselTo(index)}
                            >
                                {props.glyphs[index].name}
                            </div>
                        })}
                        <div className="addGlyphButton" onClick={() => { props.onCreate("Unnamed Glyph", "") }} >
                            <MDBIcon fas icon='plus' />
                        </div>
                    </div>
                    <div className="tableContainer" ref={sliderRef}>
                        {renderGlyphs}
                    </div>
                </div>
            </div>
        </DragDropContext>
    )
}
     